import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { AdminService } from '../../../core/services/admin.service';
import { AdminAnalyticsDto, SubscriptionPlanDto } from '../../../core/models/admin.models';
import {
  LucideAngularModule,
  Users,
  TrendingUp,
  AlertCircle,
  Clock,
  DollarSign,
  RefreshCw,
  Filter,
  X,
} from 'lucide-angular';

@Component({
  selector: 'app-admin-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, DatePickerModule],
  template: `
    <div class="p-4 sm:p-6 space-y-5">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
        <button
          (click)="load()"
          class="p-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <lucide-icon [img]="RefreshCw" class="w-4 h-4"></lucide-icon>
        </button>
      </div>

      <!-- Filters -->
      <div
        class="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
      >
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="flex flex-col gap-1">
            <label for="" class="text-xs font-medium text-gray-500 dark:text-gray-400"
              >Dan (sana)</label
            >
            <p-datepicker
              [(ngModel)]="dateFrom"
              dateFormat="dd.mm.yy"
              [showIcon]="true"
              [iconDisplay]="'input'"
              [maxDate]="dateTo"
              class="w-full"
              inputStyleClass="text-sm"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label for="" class="text-xs font-medium text-gray-500 dark:text-gray-400"
              >Gacha (sana)</label
            >
            <p-datepicker
              [(ngModel)]="dateTo"
              dateFormat="dd.mm.yy"
              [showIcon]="true"
              [iconDisplay]="'input'"
              [minDate]="dateFrom"
              class="w-full"
              inputStyleClass="text-sm"
            />
          </div>
          <div class="flex flex-col gap-1 col-span-2 lg:col-span-1">
            <label for="an-plan" class="text-xs font-medium text-gray-500 dark:text-gray-400"
              >Tarif</label
            >
            <select
              id="an-plan"
              [value]="selectedPlanId()"
              (change)="selectedPlanId.set($any($event.target).value)"
              class="h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 w-full"
            >
              <option value="">Barcha tariflar</option>
              @for (plan of plans(); track plan.id) {
                <option [value]="plan.id">{{ plan.name }}</option>
              }
            </select>
          </div>
          <div class="col-span-2 lg:col-span-1 flex gap-2 items-end">
            <button
              (click)="load()"
              class="flex-1 h-9 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <lucide-icon [img]="Filter" class="w-3.5 h-3.5"></lucide-icon>
              Filter
            </button>
            <button
              (click)="resetFilters()"
              class="h-9 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
            >
              <lucide-icon [img]="X" class="w-3.5 h-3.5"></lucide-icon>
              Reset
            </button>
          </div>
        </div>
        <p class="text-xs text-gray-400 mt-2">
          {{ dateFrom | date: 'dd.MM.yyyy' }} — {{ dateTo | date: 'dd.MM.yyyy' }}
          @if (selectedPlanId()) {
            · Tarif filtri faol
          }
        </p>
      </div>

      @if (loading()) {
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
          @for (i of [1, 2, 3, 4, 5, 6]; track i) {
            <div class="bg-white dark:bg-gray-800 rounded-xl p-5 animate-pulse h-24"></div>
          }
        </div>
      } @else if (data()) {
        <!-- Stats Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Jami mijozlar</span>
              <div
                class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="Users" class="w-4 h-4 text-blue-500"></lucide-icon>
              </div>
            </div>
            <p class="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
              {{ data()!.totalCustomers }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Faol</span>
              <div
                class="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="TrendingUp" class="w-4 h-4 text-green-500"></lucide-icon>
              </div>
            </div>
            <p
              class="text-xl sm:text-3xl font-bold text-green-600 dark:text-green-400 tabular-nums"
            >
              {{ data()!.activeCustomers }}
            </p>
            <p class="text-xs text-gray-400 mt-1">{{ activePercent() }}% jami</p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Nofaol</span>
              <div
                class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="Users" class="w-4 h-4 text-gray-400"></lucide-icon>
              </div>
            </div>
            <p class="text-xl sm:text-3xl font-bold text-gray-500 dark:text-gray-400 tabular-nums">
              {{ data()!.inactiveCustomers }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Demo mijozlar</span>
              <div
                class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="Users" class="w-4 h-4 text-blue-500"></lucide-icon>
              </div>
            </div>
            <p class="text-xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">
              {{ data()!.totalDemoCustomers }}
            </p>
            <p class="text-xs text-gray-400 mt-1">Aktiv: {{ data()!.activeDemoCustomers }}</p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400"
                >Muddati o'tgan</span
              >
              <div
                class="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="AlertCircle" class="w-4 h-4 text-red-500"></lucide-icon>
              </div>
            </div>
            <p class="text-xl sm:text-3xl font-bold text-red-600 dark:text-red-400 tabular-nums">
              {{ data()!.expiredSubscriptions }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400"
                >7 kunda tugaydi</span
              >
              <div
                class="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="Clock" class="w-4 h-4 text-amber-500"></lucide-icon>
              </div>
            </div>
            <p
              class="text-xl sm:text-3xl font-bold text-amber-600 dark:text-amber-400 tabular-nums"
            >
              {{ data()!.expiringIn7Days }}
            </p>
          </div>

          <div
            class="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-100 dark:border-gray-700"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Tushum</span>
              <div
                class="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0"
              >
                <lucide-icon [img]="DollarSign" class="w-4 h-4 text-emerald-500"></lucide-icon>
              </div>
            </div>
            <p
              class="text-base sm:text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums truncate"
            >
              {{ fmtRevenue(data()!.totalMonthlyRevenue) }} UZS
            </p>
          </div>
        </div>

        <!-- Tarif taqsimoti -->
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5"
        >
          <h2 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-4">
            Tariflar bo'yicha tushum
          </h2>
          @if (data()!.planDistribution.length === 0) {
            <p class="text-sm text-gray-400">Tanlangan davrda to'lovlar mavjud emas</p>
          } @else {
            <div class="space-y-4">
              @for (plan of data()!.planDistribution; track plan.planName; let i = $index) {
                <div>
                  <div class="flex items-center justify-between mb-1.5 gap-2">
                    <div class="flex items-center gap-2 min-w-0">
                      <span
                        class="w-2.5 h-2.5 rounded-full shrink-0"
                        [style.background-color]="planColor(i)"
                      ></span>
                      <span class="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{{
                        plan.planName
                      }}</span>
                    </div>
                    <div class="flex items-center gap-2 shrink-0 text-right">
                      <span class="text-xs text-gray-400">{{ plan.count }} ta</span>
                      <span
                        class="text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums whitespace-nowrap"
                      >
                        {{ fmtRevenue(plan.revenue) }}
                      </span>
                    </div>
                  </div>
                  <div class="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      class="h-2 rounded-full transition-all duration-500"
                      [style.width.%]="getRevenuePercent(plan.revenue)"
                      [style.background-color]="planColor(i)"
                    ></div>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Oylik registratsiyalar bar chart -->
        @if (data()!.monthlyRegistrations.length > 0) {
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5"
          >
            <h2 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-4">
              So'nggi 6 oy registratsiyalar
            </h2>
            <div class="flex items-end gap-2 h-32">
              @for (m of data()!.monthlyRegistrations; track m.month; let i = $index) {
                <div class="flex-1 flex flex-col items-center gap-1">
                  <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">{{
                    m.count
                  }}</span>
                  <div
                    class="w-full rounded-t-md transition-all duration-500"
                    [style.height.px]="barHeight(m.count)"
                    [style.background-color]="barColor(i)"
                  ></div>
                  <span class="text-[10px] text-gray-400">{{ m.month.slice(5) }}</span>
                </div>
              }
            </div>
          </div>
        }
      }
    </div>
  `,
})
export class AdminAnalyticsComponent implements OnInit {
  private adminService = inject(AdminService);
  private readonly destroyRef = inject(DestroyRef);

  data = signal<AdminAnalyticsDto | null>(null);
  plans = signal<SubscriptionPlanDto[]>([]);
  loading = signal(true);

  dateFrom: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  dateTo: Date = new Date();
  selectedPlanId = signal('');

  Users = Users;
  TrendingUp = TrendingUp;
  AlertCircle = AlertCircle;
  Clock = Clock;
  DollarSign = DollarSign;
  RefreshCw = RefreshCw;
  Filter = Filter;
  X = X;

  private readonly PALETTE = [
    '#6366f1', // indigo
    '#22c55e', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#f97316', // orange
    '#ec4899', // pink
  ];

  planColor(index: number): string {
    return this.PALETTE[index % this.PALETTE.length];
  }

  barColor(index: number): string {
    const barPalette = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    return barPalette[index % barPalette.length];
  }

  fmtRevenue(amount: number): string {
    if (amount >= 1_000_000_000) return (amount / 1_000_000_000).toFixed(1) + ' mlrd';
    if (amount >= 1_000_000) return (amount / 1_000_000).toFixed(1) + ' mln';
    if (amount >= 1_000) return (amount / 1_000).toFixed(0) + ' K';
    return amount.toLocaleString();
  }

  ngOnInit() {
    this.loadPlans();
    this.load();
  }

  loadPlans() {
    this.adminService
      .getSubscriptionPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res.success) this.plans.set(res.data);
      });
  }

  private toIso(d: Date): string {
    return d.toISOString().split('T')[0];
  }

  load() {
    this.loading.set(true);
    this.adminService
      .getAnalytics({
        dateFrom: this.toIso(this.dateFrom),
        dateTo: this.toIso(this.dateTo),
        subscriptionPlanId: this.selectedPlanId() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.data.set(res.data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  resetFilters() {
    const now = new Date();
    this.dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
    this.dateTo = new Date();
    this.selectedPlanId.set('');
    this.load();
  }

  activePercent(): number {
    const d = this.data();
    if (!d || d.totalCustomers === 0) return 0;
    return Math.round((d.activeCustomers / d.totalCustomers) * 100);
  }

  getRevenuePercent(revenue: number): number {
    const max = Math.max(...(this.data()?.planDistribution.map((p) => p.revenue) ?? [1]));
    return Math.round((revenue / (max || 1)) * 100);
  }

  barHeight(count: number): number {
    const max = Math.max(...(this.data()?.monthlyRegistrations.map((m) => m.count) ?? [1]));
    return Math.max(4, Math.round((count / (max || 1)) * 96));
  }
}
